import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUp,
  Bookmark,
  BookmarkCheck,
  CalendarRange,
  Check,
  ChevronDown,
  Compass,
  Eye,
  FileText,
  GitBranch,
  Lightbulb,
  Loader2,
  Mail,
  MessageSquare,
  Package,
  PenLine,
  Search,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';
import { clsx } from 'clsx';
import {
  agentLabApi,
  personasApi,
  type ClientPersonaSummary,
  type SkillShortcut,
  type SkillTile,
  type TileUnion,
} from '@/services/api';
import { CategoryTileCard } from '@/components/CategoryTileCard';
import { SkillPickerPanel } from '@/components/SkillPickerPanel';
import { useCreativeWorkbench } from '../CreativeWorkbenchContext';
import { RevenueIntelligenceStrip } from './RevenueIntelligenceStrip';
import { useCreativeSlashMenu } from '@/components/CreativeSlashMenu';

// ---------------------------------------------------------------------------
// Mode toggle
// ---------------------------------------------------------------------------

type HomeMode = 'explore' | 'plan' | 'create';

const MODE_LABELS: Record<HomeMode, string> = {
  explore: 'Explore',
  plan: 'Plan',
  create: 'Create',
};

function ModePill({ mode, onChange }: { mode: HomeMode; onChange: (m: HomeMode) => void }) {
  return (
    <div className="inline-flex items-center rounded-full bg-slate-100 p-0.5 gap-0.5">
      {(['explore', 'plan', 'create'] as HomeMode[]).map((m) => (
        <button
          key={m}
          onClick={() => onChange(m)}
          className={clsx(
            'rounded-full px-4 py-1.5 text-xs font-semibold transition-all',
            mode === m ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700',
          )}
        >
          {MODE_LABELS[m]}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quick-launch pill strip — mode-specific, max 3
// ---------------------------------------------------------------------------

interface QuickSkill {
  id: string;
  label: string;
  Icon: React.ElementType;
  prompt: string;
}

const QUICK_SKILLS_BY_MODE: Record<HomeMode, QuickSkill[]> = {
  explore: [
    { id: 'ask_persona',       label: 'Ask the Persona',   Icon: MessageSquare, prompt: 'What motivates our top persona to make a purchase decision?' },
    { id: 'scenario_explorer', label: 'Decision Map',      Icon: GitBranch,     prompt: 'Show me the decision architecture and buyer scenarios for our top persona.' },
    { id: 'ask_persona',       label: 'Compare Personas',  Icon: Users,         prompt: 'Compare the top two personas — what are their key differences in buying behavior?' },
  ],
  plan: [
    { id: 'suggest_briefs',      label: 'Prioritize Creatives', Icon: Target,        prompt: 'What should our next 3 creative priorities be based on our top persona insights?' },
    { id: 'creative_brief',      label: 'Email Drip Campaign',  Icon: Mail,          prompt: 'Create a unified email drip campaign targeting our highest-converting persona.' },
    { id: 'seo_keyword_strategy',label: 'Keyword Strategy',     Icon: Search,        prompt: "Build a keyword strategy grounded in our top persona's purchase decision ontology." },
  ],
  create: [
    { id: 'draft_brief',    label: 'Draft a Brief',    Icon: PenLine,  prompt: 'Draft a creative brief for our top-performing persona and product.' },
    { id: 'ad_pack',        label: 'Generate Ad Pack', Icon: Package,  prompt: 'Generate a complete ad pack with static image ads for our top persona.' },
    { id: 'creative_review',label: 'Review an Asset',  Icon: Eye,      prompt: "Review a creative asset through our top persona's cognitive and emotional lens." },
  ],
};

function QuickPill({
  skill,
  mode,
  onClick,
}: {
  skill: QuickSkill;
  mode: HomeMode;
  onClick: (s: QuickSkill) => void;
}) {
  const accentClass =
    mode === 'explore'
      ? 'hover:border-purple-300 hover:text-purple-600 hover:bg-purple-50/50'
      : mode === 'plan'
      ? 'hover:border-amber-300 hover:text-amber-600 hover:bg-amber-50/50'
      : 'hover:border-brand-teal/40 hover:text-brand-teal hover:bg-teal-50/50';

  return (
    <button
      onClick={() => onClick(skill)}
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-all',
        'border-slate-200 bg-white text-slate-600',
        accentClass,
      )}
    >
      <skill.Icon size={12} className="shrink-0" />
      {skill.label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// NeuroGraph mark — tinted per mode
// ---------------------------------------------------------------------------

const MODE_MARK_COLORS: Record<HomeMode, { bg: string; icon: string }> = {
  explore: { bg: 'from-purple-200/60 to-purple-50',  icon: 'text-purple-500' },
  plan:    { bg: 'from-amber-200/60  to-amber-50',   icon: 'text-amber-500'  },
  create:  { bg: 'from-brand-teal/20 to-brand-teal/5', icon: 'text-brand-teal' },
};

function NeuroMark({ mode }: { mode: HomeMode }) {
  const { bg, icon } = MODE_MARK_COLORS[mode];
  return (
    <div className={clsx('w-10 h-10 rounded-xl bg-gradient-to-br flex items-center justify-center mx-auto transition-all duration-300', bg)}>
      <svg viewBox="0 0 24 24" fill="none" className={clsx('w-5 h-5 transition-colors duration-300', icon)} aria-hidden>
        <path
          d="M12 2v20M2 12h20M4.93 4.93l14.14 14.14M19.07 4.93 4.93 19.07"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Suggested skill cards — elegant horizontal rail
// ---------------------------------------------------------------------------

const CATEGORY_ICON_BG: Record<string, string> = {
  insight:  'bg-purple-50  text-purple-500',
  creative: 'bg-teal-50    text-brand-teal',
  seo:      'bg-emerald-50 text-emerald-600',
  cro:      'bg-rose-50    text-rose-500',
  paid_ads: 'bg-amber-50   text-amber-500',
  display:  'bg-slate-100  text-slate-500',
};

const CATEGORY_LABEL: Record<string, string> = {
  insight:  'Insight',
  creative: 'Creative',
  seo:      'SEO',
  cro:      'CRO',
  paid_ads: 'Paid',
  display:  'Display',
};

const CATEGORY_LABEL_COLOR: Record<string, string> = {
  insight:  'text-purple-500',
  creative: 'text-brand-teal',
  seo:      'text-emerald-600',
  cro:      'text-rose-500',
  paid_ads: 'text-amber-500',
  display:  'text-slate-500',
};

const OUTPUT_LABELS: Record<string, string> = {
  artifact: 'artifact',
  analysis: 'analysis',
  list:     'list',
  brief:    'brief',
};

const SKILL_ICON_MAP: Record<string, React.ElementType> = {
  MessageSquare,
  Lightbulb,
  GitBranch,
  FileText,
  Eye,
  Search,
  Zap,
  Users,
  Compass,
  Target,
  Mail,
  CalendarRange,
  Package,
  PenLine,
  TrendingUp,
};

/** Static curated tiles per mode — shown when no client is selected or as fallback */
const STATIC_TILES_BY_MODE: Record<HomeMode, Omit<SkillTile, 'context' | 'color_scheme'>[]> = {
  explore: [
    {
      skill_id: 'ask_persona',
      skill_name: 'Ask the Persona',
      pitch: 'Deep Q&A grounded in cognitive graph data — decision factors, buyer scenarios, and intent signals.',
      category: 'insight',
      icon: 'MessageSquare',
      trigger_prompt: 'I want to ask a persona a question. Which client and persona should we use?',
      estimated_output: 'analysis',
      estimated_seconds: 15,
    },
    {
      skill_id: 'scenario_explorer',
      skill_name: 'Decision Architecture',
      pitch: "Render the persona's causal decision graph: PDO concepts, edges, and buyer scenarios.",
      category: 'insight',
      icon: 'GitBranch',
      trigger_prompt: 'Show me the decision architecture for a persona. Which client should we explore?',
      estimated_output: 'artifact',
      estimated_seconds: 12,
    },
    {
      skill_id: 'ask_persona',
      skill_name: 'Persona Deep Dive',
      pitch: "Get rich context on what drives a persona's buying decisions, values, and barriers.",
      category: 'insight',
      icon: 'Users',
      trigger_prompt: "Give me a deep dive on our top persona — their values, purchase drivers, and biggest barriers.",
      estimated_output: 'analysis',
      estimated_seconds: 18,
    },
    {
      skill_id: 'scenario_explorer',
      skill_name: 'Buyer Scenarios',
      pitch: 'Explore all resolved buyer scenarios and how they connect to keyword intent clusters.',
      category: 'insight',
      icon: 'Compass',
      trigger_prompt: "Show me all buyer scenarios and intent signals for our top persona.",
      estimated_output: 'artifact',
      estimated_seconds: 12,
    },
    {
      skill_id: 'seo_keyword_strategy',
      skill_name: 'Intent Surface',
      pitch: "Map the keyword intent surface emerging from the persona's PDO concept activations.",
      category: 'seo',
      icon: 'Search',
      trigger_prompt: "Show me the full keyword intent surface for our top persona.",
      estimated_output: 'list',
      estimated_seconds: 20,
    },
  ],
  plan: [
    {
      skill_id: 'suggest_briefs',
      skill_name: 'Prioritize Activities',
      pitch: 'Get ranked creative priorities grounded in buyer scenarios and top persona insights.',
      category: 'creative',
      icon: 'Target',
      trigger_prompt: "What should our next 3 creative priorities be based on our top persona insights?",
      estimated_output: 'list',
      estimated_seconds: 20,
    },
    {
      skill_id: 'creative_brief',
      skill_name: 'Email Drip Campaign',
      pitch: 'Design a unified email drip sequence aligned to buyer scenario stage gates.',
      category: 'creative',
      icon: 'Mail',
      trigger_prompt: "Create a unified email drip campaign targeting our highest-converting persona.",
      estimated_output: 'brief',
      estimated_seconds: 35,
    },
    {
      skill_id: 'seo_keyword_strategy',
      skill_name: 'Keyword Strategy',
      pitch: 'Derive a persona-grounded keyword cluster from the Purchase Decision Ontology.',
      category: 'seo',
      icon: 'Search',
      trigger_prompt: "Build a keyword strategy for a client using their PDO. Which client and persona?",
      estimated_output: 'list',
      estimated_seconds: 25,
    },
    {
      skill_id: 'suggest_briefs',
      skill_name: 'Campaign Roadmap',
      pitch: 'Turn persona insights and buyer scenarios into a phased campaign calendar.',
      category: 'creative',
      icon: 'CalendarRange',
      trigger_prompt: "Build a 90-day creative campaign roadmap based on our top persona's buyer scenarios.",
      estimated_output: 'list',
      estimated_seconds: 30,
    },
    {
      skill_id: 'meta_ads',
      skill_name: 'Paid Media Plan',
      pitch: 'Structure paid channels, audiences, and budgets around the persona decision model.',
      category: 'paid_ads',
      icon: 'Zap',
      trigger_prompt: "Create a paid media plan for Meta and Google targeting our top persona.",
      estimated_output: 'analysis',
      estimated_seconds: 25,
    },
  ],
  create: [
    {
      skill_id: 'draft_brief',
      skill_name: 'Draft a Brief',
      pitch: 'Generate a 3-section creative brief with PDO-enriched angles — fast, no Firestore write.',
      category: 'creative',
      icon: 'FileText',
      trigger_prompt: 'Draft a creative brief for a campaign. Which client and creative direction are we starting from?',
      estimated_output: 'brief',
      estimated_seconds: 30,
    },
    {
      skill_id: 'ad_pack',
      skill_name: 'Generate Ad Pack',
      pitch: 'Produce a complete set of static image ads tailored to the persona and campaign angle.',
      category: 'creative',
      icon: 'Package',
      trigger_prompt: 'Generate a complete ad pack with static image ads for our top persona.',
      estimated_output: 'artifact',
      estimated_seconds: 60,
    },
    {
      skill_id: 'creative_review',
      skill_name: 'Review an Ad',
      pitch: "Review any ad, email, or landing page through the persona's cognitive and emotional lens.",
      category: 'creative',
      icon: 'Eye',
      trigger_prompt: "I want to review a creative asset through a persona's lens. Share the asset or URL.",
      estimated_output: 'analysis',
      estimated_seconds: 20,
    },
    {
      skill_id: 'generate_angles',
      skill_name: 'Creative Angles',
      pitch: 'Generate 3–8 creative angles with hook, message, emotional lever, and rationale.',
      category: 'creative',
      icon: 'Lightbulb',
      trigger_prompt: "Generate creative angles for a campaign targeting our top persona.",
      estimated_output: 'list',
      estimated_seconds: 20,
    },
    {
      skill_id: 'generate_script',
      skill_name: 'Write a Script',
      pitch: 'Generate a segmented UGC, testimonial, or tutorial script from a creative angle.',
      category: 'creative',
      icon: 'PenLine',
      trigger_prompt: "Write a UGC script for our top persona and best creative angle.",
      estimated_output: 'brief',
      estimated_seconds: 25,
    },
  ],
};

interface SuggestedSkillCardProps {
  tile: SkillTile | (typeof STATIC_TILES_BY_MODE)[HomeMode][number];
  onLaunch: (prompt: string, skillId: string) => void;
  onPin?: (tile: SkillTile) => Promise<void>;
  alreadyPinned?: boolean;
  pinnable?: boolean;
}

function SuggestedSkillCard({ tile, onLaunch, onPin, alreadyPinned, pinnable }: SuggestedSkillCardProps) {
  const IconComponent = SKILL_ICON_MAP[tile.icon] ?? Zap;
  const catLabel = CATEGORY_LABEL[tile.category] ?? tile.category;
  const catLabelColor = CATEGORY_LABEL_COLOR[tile.category] ?? 'text-slate-500';
  const iconBg = CATEGORY_ICON_BG[tile.category] ?? 'bg-slate-100 text-slate-500';
  const outputLabel = OUTPUT_LABELS[tile.estimated_output] ?? tile.estimated_output;
  const [pinning, setPinning] = useState(false);
  const [pinned, setPinned] = useState(Boolean(alreadyPinned));

  const handlePin = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onPin || pinned) return;
    setPinning(true);
    try {
      await onPin(tile as SkillTile);
      setPinned(true);
    } catch {
      // silent — outer toast handler can surface errors
    } finally {
      setPinning(false);
    }
  };

  return (
    <button
      type="button"
      onClick={() => onLaunch(tile.trigger_prompt, tile.skill_id)}
      className={clsx(
        'group relative flex flex-col text-left rounded-2xl border border-slate-200 bg-white',
        'px-5 py-5 transition-all duration-200 shrink-0 w-56',
        'hover:border-slate-300 hover:shadow-[0_4px_20px_-4px_rgba(0,0,0,0.08)]',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal/40',
      )}
    >
      {/* Pin button — only visible on hover, only on dynamic (smart) tiles */}
      {pinnable && (
        <button
          type="button"
          onClick={handlePin}
          title={pinned ? 'Shortcut saved' : 'Pin as shortcut'}
          aria-label={pinned ? 'Shortcut saved' : 'Pin as shortcut'}
          className={clsx(
            'absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-md transition-all',
            'opacity-0 group-hover:opacity-100',
            pinned
              ? 'text-brand-teal bg-brand-teal/10'
              : 'text-slate-400 hover:text-brand-teal hover:bg-brand-teal/10',
          )}
        >
          {pinning ? (
            <Loader2 size={11} className="animate-spin" />
          ) : pinned ? (
            <BookmarkCheck size={12} />
          ) : (
            <Bookmark size={12} />
          )}
        </button>
      )}

      {/* Top row: icon circle + category label + time */}
      <div className="flex items-center gap-2.5 mb-3.5">
        <div className={clsx('flex items-center justify-center w-7 h-7 rounded-lg shrink-0', iconBg)}>
          <IconComponent size={14} />
        </div>
        <span className={clsx('text-[10px] font-bold uppercase tracking-widest', catLabelColor)}>
          {catLabel}
        </span>
        <span className="ml-auto text-[10px] text-slate-300 font-mono tabular-nums shrink-0">
          ~{tile.estimated_seconds}s
        </span>
      </div>

      {/* Skill name */}
      <p className="text-sm font-semibold text-slate-800 leading-snug mb-2 group-hover:text-brand-teal transition-colors duration-150">
        {tile.skill_name}
      </p>

      {/* Pitch */}
      <p className="text-[12px] text-slate-400 leading-relaxed line-clamp-2 flex-1">
        {tile.pitch}
      </p>

      {/* Footer: output type + arrow */}
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
        <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">
          {outputLabel}
        </span>
        <span
          className={clsx(
            'flex items-center justify-center w-5 h-5 rounded-full transition-all duration-200',
            'bg-slate-100 text-slate-400',
            'group-hover:bg-brand-teal group-hover:text-white group-hover:translate-x-0.5',
          )}
        >
          <ArrowRight size={10} />
        </span>
      </div>
    </button>
  );
}

function SuggestedSkillCardSkeleton() {
  return (
    <div className="shrink-0 w-56 rounded-2xl border border-slate-100 bg-white px-5 py-5 animate-pulse">
      <div className="flex items-center gap-2.5 mb-3.5">
        <div className="w-7 h-7 rounded-lg bg-slate-100" />
        <div className="h-2.5 w-14 rounded-full bg-slate-100" />
      </div>
      <div className="h-4 w-4/5 rounded bg-slate-100 mb-2" />
      <div className="space-y-1.5 mb-4">
        <div className="h-3 w-full rounded bg-slate-50" />
        <div className="h-3 w-3/4 rounded bg-slate-50" />
      </div>
      <div className="flex items-center justify-between pt-3 border-t border-slate-100">
        <div className="h-2.5 w-10 rounded bg-slate-100" />
        <div className="w-5 h-5 rounded-full bg-slate-100" />
      </div>
    </div>
  );
}

interface SuggestedSkillsRailProps {
  mode: HomeMode;
  clientUid?: string;
  personaId?: string;
  personaName?: string;
  /** Personas available for the current client — feeds the inline selector. */
  personas?: ClientPersonaSummary[];
  /** Switch active persona — empty/null clears the selection. */
  onSelectPersona?: (personaId: string | null) => void;
  /** Indicates the persona list is still loading. */
  personasLoading?: boolean;
  onLaunch: (prompt: string, skillId: string, signalLabel?: string) => void;
}

const MODE_SPARKLE_COLOR: Record<HomeMode, string> = {
  explore: 'text-purple-400',
  plan:    'text-amber-400',
  create:  'text-brand-teal',
};

const MODE_LABEL_ACCENT: Record<HomeMode, string> = {
  explore: 'text-purple-500 hover:text-purple-700',
  plan:    'text-amber-600 hover:text-amber-700',
  create:  'text-brand-teal hover:text-brand-teal/80',
};

/** Source badge rendered next to the section header */
type TileSource = 'smart' | 'rule' | 'static';

// ---------------------------------------------------------------------------
// Inline persona selector — looks like inline text with a caret + dotted
// underline. Click to open a dropdown of available personas for the client.
// Used inside the rail header so users can swap target persona without
// leaving the page.
// ---------------------------------------------------------------------------

interface PersonaInlineSelectProps {
  personas: ClientPersonaSummary[];
  currentPersonaId: string | null;
  currentPersonaName?: string;
  loading?: boolean;
  onChange: (personaId: string) => void;
  /** Tailwind color class for the persona name label (matches mode accent) */
  accentClass?: string;
}

function PersonaInlineSelect({
  personas,
  currentPersonaId,
  currentPersonaName,
  loading,
  onChange,
  accentClass = 'text-slate-500',
}: PersonaInlineSelectProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const label = currentPersonaName
    ?? personas.find((p) => p.id === currentPersonaId)?.name
    ?? (loading ? 'Loading…' : 'Select persona');

  return (
    <div ref={wrapRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={loading || personas.length === 0}
        className={clsx(
          'inline-flex items-baseline gap-1 font-semibold uppercase tracking-widest',
          'border-b border-dotted border-slate-300 hover:border-slate-500 transition-colors',
          'focus:outline-none focus-visible:border-slate-500',
          'disabled:opacity-60 disabled:cursor-default disabled:hover:border-slate-300',
          accentClass,
        )}
      >
        <span>{label}</span>
        <ChevronDown
          size={10}
          className={clsx(
            'shrink-0 self-center transition-transform duration-150',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && personas.length > 0 && (
        <div
          className={clsx(
            'absolute z-30 left-0 top-full mt-2 min-w-[220px] max-h-72 overflow-y-auto',
            'rounded-xl border border-slate-200 bg-white shadow-lg py-1',
          )}
        >
          {personas.map((p) => {
            const isActive = p.id === currentPersonaId;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => { onChange(p.id); setOpen(false); }}
                className={clsx(
                  'flex w-full items-center gap-2 px-3 py-2 text-left text-xs transition-colors',
                  isActive
                    ? 'bg-slate-50 text-slate-800 font-semibold'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800',
                )}
              >
                <span className="flex-1 truncate normal-case tracking-normal">
                  {p.name}
                </span>
                {isActive && <Check size={12} className="text-brand-teal shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


function shortcutToTile(s: SkillShortcut): SkillTile & { _shortcut_id?: string } {
  return {
    skill_id: s.skill_id_ref,
    skill_name: s.name,
    pitch: s.pitch,
    trigger_prompt: s.trigger_prompt,
    category: (s.ui?.category as string) ?? 'creative',
    icon: (s.ui?.icon as string) ?? 'Zap',
    color_scheme: (s.ui?.color_scheme as string) ?? 'teal',
    estimated_output: ((s.ui?.estimated_output as SkillTile['estimated_output']) ?? 'analysis'),
    estimated_seconds: (s.ui?.estimated_seconds as number) ?? 20,
    context: { client_uid: s.client_uid, persona_id: s.persona_id ?? '', source: 'shortcut' },
    _shortcut_id: s.shortcut_id,
  };
}

function SuggestedSkillsRail({
  mode,
  clientUid,
  personaId,
  personaName: personaNameProp,
  personas = [],
  personasLoading = false,
  onSelectPersona,
  onLaunch,
}: SuggestedSkillsRailProps) {
  const [tiles, setTiles] = useState<TileUnion[]>([]);
  const [pinnedSet, setPinnedSet] = useState<Set<string>>(new Set());
  const [resolvedPersonaName, setResolvedPersonaName] = useState<string | undefined>(personaNameProp);
  /** persona_id auto-resolved by the smart-tiles backend when caller omits one. */
  const [resolvedPersonaId, setResolvedPersonaId] = useState<string | undefined>(undefined);
  const [tileSource, setTileSource] = useState<TileSource>('static');
  const [loading, setLoading] = useState(false);
  /** skill_id of the CategoryTile whose picker panel is currently open */
  const [openCategoryId, setOpenCategoryId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!clientUid) {
      setTiles([]);
      setPinnedSet(new Set());
      setTileSource('static');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setOpenCategoryId(null);

    // Phase 3 § 7.5: pinned shortcuts and smart tiles are fetched
    // concurrently; shortcuts always render first (they are deliberate
    // user preferences) and smart tiles are deduped against them by
    // trigger_prompt.
    Promise.allSettled([
      agentLabApi.listShortcuts(clientUid),
      agentLabApi.getSmartTiles(clientUid, personaId, 4, mode),
    ])
      .then(async ([shortcutsResult, smartResult]) => {
        if (cancelled) return;
        const shortcutTiles: SkillTile[] = shortcutsResult.status === 'fulfilled'
          ? (shortcutsResult.value.shortcuts ?? []).map(shortcutToTile)
          : [];
        const pinnedPrompts = new Set(shortcutTiles.map((t) => t.trigger_prompt));
        setPinnedSet(pinnedPrompts);

        let smartTiles: TileUnion[] = [];
        let detectedSmart = false;
        let resolvedName: string | undefined;
        let resolvedId: string | undefined;
        if (smartResult.status === 'fulfilled' && smartResult.value.tiles?.length) {
          smartTiles = smartResult.value.tiles;
          detectedSmart = smartTiles.some(
            (t) => t.tile_type !== 'category' && ((t as SkillTile).context as Record<string, unknown>)?.source === 'smart',
          );
          if (smartResult.value.persona_id) resolvedId = smartResult.value.persona_id;
          if (smartResult.value.persona_name && smartResult.value.persona_name !== personaId) {
            resolvedName = smartResult.value.persona_name;
          }
        }

        // If smart tiles failed entirely AND there are no shortcuts, fall back to rule-based
        if (smartTiles.length === 0 && shortcutTiles.length === 0) {
          try {
            const ruleRes = await agentLabApi.getSkillTiles(clientUid, personaId, 5, mode);
            if (!cancelled) {
              setTiles(ruleRes.tiles ?? []);
              setTileSource('rule');
              if (ruleRes.persona_id) setResolvedPersonaId(ruleRes.persona_id);
              if (ruleRes.persona_name && ruleRes.persona_name !== personaId) {
                setResolvedPersonaName(ruleRes.persona_name);
              }
            }
          } catch {
            if (!cancelled) setTiles([]);
          }
          return;
        }

        // Dedup direct smart tiles against pinned shortcuts; category tiles always pass through.
        const dedupedSmart = smartTiles.filter((t) =>
          t.tile_type === 'category' || !pinnedPrompts.has((t as SkillTile).trigger_prompt),
        );
        const final: TileUnion[] = [...shortcutTiles.map((t) => ({ ...t, tile_type: 'direct' as const })), ...dedupedSmart];
        setTiles(final);
        setTileSource(shortcutTiles.length > 0 ? 'smart' : detectedSmart ? 'smart' : 'rule');
        if (resolvedId) setResolvedPersonaId(resolvedId);
        if (resolvedName) setResolvedPersonaName(resolvedName);
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [clientUid, personaId, mode]);

  const handlePinTile = async (tile: SkillTile) => {
    if (!clientUid) return;
    await agentLabApi.pinSkill({
      skill_id_ref: tile.skill_id,
      name: tile.skill_name,
      pitch: tile.pitch,
      trigger_prompt: tile.trigger_prompt,
      client_uid: tile.context?.client_uid ?? clientUid,
      persona_id: (tile.context?.persona_id as string) ?? personaId,
      ui: {
        category: tile.category,
        color_scheme: tile.color_scheme,
        icon: tile.icon,
        estimated_output: tile.estimated_output,
        estimated_seconds: tile.estimated_seconds,
        post_actions: [],
      },
    });
    setPinnedSet((prev) => new Set(prev).add(tile.trigger_prompt));
  };

  // Sync prop changes
  useEffect(() => { setResolvedPersonaName(personaNameProp); }, [personaNameProp]);

  const staticTiles = STATIC_TILES_BY_MODE[mode];
  const displayTiles: TileUnion[] = tiles.length > 0
    ? tiles
    : staticTiles.map((t) => ({ ...t, tile_type: 'direct' as const, context: { client_uid: '', persona_id: '' }, color_scheme: 'teal' }));
  const isDynamic = tiles.length > 0;

  const openCategoryTile = displayTiles.find(
    (t) => t.tile_type === 'category' && t.skill_id === openCategoryId,
  );

  const modePrefix =
    mode === 'explore' ? 'Explore'
    : mode === 'plan'  ? 'Plan'
    :                    'Create';

  /**
   * The id we should treat as "currently active" in the UI:
   * 1. Explicit user selection via workbench context
   * 2. Else the server-side auto-pick from the most recent tiles response
   */
  const effectivePersonaId = personaId || resolvedPersonaId || null;

  /**
   * Display name. Prefer the canonical name from the persona list (which is
   * the human-friendly NeuroGraph name) over the server's title-cased
   * fallback so we never render an opaque slug like "Pid 2Aqezfmnoociehscibm6K".
   */
  const personaNameFromList = effectivePersonaId
    ? personas.find((p) => p.id === effectivePersonaId)?.name
    : undefined;
  const displayPersonaName =
    personaNameFromList
    ?? (resolvedPersonaName && !/^pid\s/i.test(resolvedPersonaName) ? resolvedPersonaName : undefined);

  const sourceBadge =
    tileSource === 'smart' ? { label: 'AI', title: 'Personalized by NeuroGraph agent' }
    : tileSource === 'rule' ? { label: 'live', title: 'Contextual rule-based suggestions' }
    : null;

  // Display "Client Name" as a friendlier header chunk when we have it from
  // a smart-tiles response; otherwise fall back to the slug (uppercased).
  const clientLabel = clientUid ?? '';

  const showInlineSelector =
    isDynamic && Boolean(clientUid) && (personas.length > 0 || personasLoading);

  return (
    <div className="w-full max-w-3xl">
      {/* Section header */}
      <div className="flex items-center gap-2 mb-4 px-1 flex-wrap">
        <Sparkles size={12} className={clsx('shrink-0 transition-colors duration-300', MODE_SPARKLE_COLOR[mode])} />
        <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
          {modePrefix} ·
        </span>

        {showInlineSelector ? (
          <PersonaInlineSelect
            personas={personas}
            currentPersonaId={effectivePersonaId}
            currentPersonaName={displayPersonaName}
            loading={personasLoading}
            onChange={(id) => onSelectPersona?.(id)}
            accentClass={clsx('text-[11px]', MODE_LABEL_ACCENT[mode])}
          />
        ) : (
          <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
            {displayPersonaName || (clientUid ? 'suggested' : 'suggested skills')}
          </span>
        )}

        {clientLabel && (
          <>
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
              ×
            </span>
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
              {clientLabel}
            </span>
          </>
        )}

        {loading && clientUid && (
          <span className="text-[10px] text-slate-300 ml-1">— loading…</span>
        )}
        {!loading && sourceBadge && (
          <span
            title={sourceBadge.title}
            className={clsx(
              'ml-1 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full',
              tileSource === 'smart'
                ? 'bg-purple-50 text-purple-400 border border-purple-100'
                : 'bg-slate-50 text-slate-300 border border-slate-100',
            )}
          >
            {sourceBadge.label}
          </span>
        )}
      </div>

      {/* Horizontal scroll rail */}
      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto pb-2 scroll-smooth"
        style={{ scrollbarWidth: 'none' }}
      >
        {loading
          ? Array.from({ length: 5 }).map((_, i) => <SuggestedSkillCardSkeleton key={i} />)
          : displayTiles.map((tile, idx) => {
              if (tile.tile_type === 'category') {
                return (
                  <CategoryTileCard
                    key={`${tile.skill_id}-${idx}`}
                    tile={tile}
                    isOpen={openCategoryId === tile.skill_id}
                    onToggle={() => setOpenCategoryId((prev) => prev === tile.skill_id ? null : tile.skill_id)}
                    disabled={false}
                  />
                );
              }
              const t = tile as SkillTile;
              // Only dynamic, real tiles can be pinned (static fallbacks have no
              // analytics-backed trigger prompt to preserve).
              const canPin = isDynamic && Boolean(clientUid);
              const already = pinnedSet.has(t.trigger_prompt);
              return (
                <SuggestedSkillCard
                  key={`${tile.skill_id}-${idx}`}
                  tile={t}
                  onLaunch={onLaunch}
                  onPin={canPin ? handlePinTile : undefined}
                  pinnable={canPin}
                  alreadyPinned={already}
                />
              );
            })}

        {/* Trailing spacer so last card doesn't touch the edge */}
        <div className="shrink-0 w-1" aria-hidden />
      </div>

      {/* SkillPickerPanel — expands below the scroll rail when a category tile is open */}
      {openCategoryTile && openCategoryTile.tile_type === 'category' && (
        <SkillPickerPanel
          tile={openCategoryTile}
          isOpen={true}
          onLaunch={(prompt, skillId, signalLabel) => {
            setOpenCategoryId(null);
            onLaunch(prompt, skillId, signalLabel);
          }}
          onClose={() => setOpenCategoryId(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Placeholder copy per mode
// ---------------------------------------------------------------------------

const MODE_META: Record<HomeMode, { placeholder: string; headline: string; cta: string; hint: string }> = {
  explore: {
    headline: 'What are we exploring today?',
    placeholder: 'Ask about a persona, buyer scenario, decision factor, or market signal…',
    cta: 'Explore',
    hint: '⌘↵ to send · type / for Creative Skills · powered by NeuroGraph MCP',
  },
  plan: {
    headline: "What's the next move?",
    placeholder: 'Describe a strategic goal, campaign objective, or creative challenge to plan around…',
    cta: 'Start planning',
    hint: '⌘↵ to send · type / for Creative Skills · grounded in persona & PDO data',
  },
  create: {
    headline: "Let's build something.",
    placeholder: 'Describe the campaign direction, product, or audience you want to brief…',
    cta: 'Create',
    hint: '⌘↵ to continue · type / for Creative Skills · guided brief wizard',
  },
};

// ---------------------------------------------------------------------------
// Input border accent per mode
// ---------------------------------------------------------------------------

const MODE_INPUT_BORDER: Record<HomeMode, { idle: string; focus: string }> = {
  explore: { idle: 'border-purple-200/60',  focus: 'focus-within:border-purple-300' },
  plan:    { idle: 'border-amber-200/60',   focus: 'focus-within:border-amber-300'  },
  create:  { idle: 'border-brand-teal/30',  focus: 'focus-within:border-brand-teal/50' },
};

const MODE_SUBMIT_COLOR: Record<HomeMode, { active: string }> = {
  explore: { active: 'bg-purple-500 text-white hover:bg-purple-600' },
  plan:    { active: 'bg-amber-500  text-white hover:bg-amber-600'  },
  create:  { active: 'bg-brand-teal text-white hover:bg-brand-teal/90' },
};

// ---------------------------------------------------------------------------
// Main HomeView
// ---------------------------------------------------------------------------

export function HomeView() {
  const navigate = useNavigate();
  const { clientUid, personaId, setPersona } = useCreativeWorkbench();

  const [mode, setMode] = useState<HomeMode>('explore');
  const [input, setInput] = useState('');
  const [cursorPos, setCursorPos] = useState(0);
  const [personas, setPersonas] = useState<ClientPersonaSummary[]>([]);
  const [personasLoading, setPersonasLoading] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { handleKeyDown: slashKeyDown, SlashMenuPortal } = useCreativeSlashMenu({
    composerRef: textareaRef,
    value: input,
    cursorPos,
    clientUid,
    personaId,
    mode,
    onValueChange: (next, pos) => {
      setInput(next);
      if (pos !== undefined) {
        requestAnimationFrame(() => {
          const el = textareaRef.current;
          if (el) {
            el.setSelectionRange(pos, pos);
            setCursorPos(pos);
          }
        });
      }
    },
    onPick: (pick) => {
      const params = new URLSearchParams({
        prompt: pick.prompt,
        skill: pick.skillId,
        ...(clientUid ? { client: clientUid } : {}),
        ...(personaId ? { persona: personaId } : {}),
        ...(pick.signal ? { signal: pick.signal } : {}),
      });
      navigate(`/chat/new?${params.toString()}`);
    },
  });

  // Fetch the canonical persona list for the active client. Used to:
  //   1. Render the inline persona switcher next to "EXPLORE ·"
  //   2. Substitute the human-readable persona name in the rail header
  //      (avoids ever showing an opaque NeuroGraph slug like
  //      "PID 2AQEZFMNOOCIEHSCIBM6K" through CSS uppercase).
  useEffect(() => {
    if (!clientUid) {
      setPersonas([]);
      return;
    }
    let cancelled = false;
    setPersonasLoading(true);
    personasApi
      .listForClient(clientUid)
      .then((res) => {
        if (!cancelled) setPersonas(res.personas ?? []);
      })
      .catch(() => {
        if (!cancelled) setPersonas([]);
      })
      .finally(() => {
        if (!cancelled) setPersonasLoading(false);
      });
    return () => { cancelled = true; };
  }, [clientUid]);

  const meta = MODE_META[mode];

  // Reset input when mode changes so placeholder feels fresh
  const handleModeChange = (m: HomeMode) => {
    setMode(m);
    setInput('');
    setCursorPos(0);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    setCursorPos(e.target.selectionStart ?? 0);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  };

  const buildChatParams = (prompt: string, skillId?: string) => {
    const params = new URLSearchParams({ prompt });
    if (skillId) params.set('skill', skillId);
    if (clientUid) params.set('client', clientUid);
    if (personaId) params.set('persona', personaId);
    return params.toString();
  };

  const handleSubmit = () => {
    const text = input.trim();
    if (!text) return;
    if (mode === 'create') {
      const params = new URLSearchParams({ prompt: text });
      if (clientUid) params.set('client', clientUid);
      navigate(`/creative/briefs?${params.toString()}`);
    } else {
      navigate(`/chat/new?${buildChatParams(text)}`);
    }
  };

  const handleComposerKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (slashKeyDown(e)) return;
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && input.trim()) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleQuickSkill = (skill: QuickSkill) => {
    if (mode === 'create') {
      setInput(skill.prompt);
      textareaRef.current?.focus();
    } else {
      navigate(`/chat/new?${buildChatParams(skill.prompt, skill.id)}`);
    }
  };

  const handleTileLaunch = (prompt: string, skillId: string, signalLabel?: string) => {
    const params = new URLSearchParams(buildChatParams(prompt, skillId));
    if (signalLabel) params.set('signal', signalLabel);
    navigate(`/chat/new?${params.toString()}`);
  };

  const borderColors = MODE_INPUT_BORDER[mode];
  const submitColors = MODE_SUBMIT_COLOR[mode];
  const quickSkills = QUICK_SKILLS_BY_MODE[mode];

  return (
    <div className="flex flex-col items-center px-4 pt-16 pb-20 gap-10">

      {/* ── Center hero ──────────────────────────────────────────────── */}
      <div className="w-full max-w-2xl flex flex-col items-center gap-8">

        {/* Brand mark + headline + toggle */}
        <div className="flex flex-col items-center gap-4 text-center">
          <NeuroMark mode={mode} />
          <h1 className="text-2xl font-semibold text-slate-800 tracking-tight transition-all duration-300">
            {meta.headline}
          </h1>
          <ModePill mode={mode} onChange={handleModeChange} />
        </div>

        {/* Input card */}
        <div
          className={clsx(
            'relative w-full rounded-2xl border bg-white shadow-sm transition-all duration-200',
            'focus-within:shadow-md',
            borderColors.idle,
            borderColors.focus,
          )}
        >
          <textarea
            ref={textareaRef}
            value={input}
            onChange={handleInputChange}
            onSelect={(e) => setCursorPos(e.currentTarget.selectionStart ?? 0)}
            onKeyUp={(e) => setCursorPos((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
            onClick={(e) => setCursorPos((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
            onKeyDown={handleComposerKeyDown}
            placeholder={meta.placeholder}
            rows={3}
            className="w-full resize-none rounded-2xl bg-transparent px-5 pt-4 pb-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none"
            style={{ maxHeight: 200 }}
          />

          <div className="flex items-center justify-between px-4 pb-3">
            <div className="flex items-center gap-2">
              {clientUid ? (
                <span className="text-[11px] font-mono text-slate-400 bg-slate-50 border border-slate-100 rounded-full px-2.5 py-0.5">
                  {clientUid}
                </span>
              ) : (
                <span className="text-[11px] text-slate-300">No client selected</span>
              )}
              {personaId && (
                <span className="text-[11px] text-purple-500 bg-purple-50 border border-purple-100 rounded-full px-2.5 py-0.5">
                  {personas.find((p) => p.id === personaId)?.name ?? personaId}
                </span>
              )}
            </div>

            <button
              onClick={handleSubmit}
              disabled={!input.trim()}
              title={meta.cta}
              className={clsx(
                'flex items-center justify-center w-8 h-8 rounded-full transition-all duration-150',
                input.trim() ? submitColors.active : 'bg-slate-100 text-slate-300 cursor-not-allowed',
              )}
            >
              <ArrowUp size={15} />
            </button>
          </div>
        </div>

        {/* Hint line */}
        <p className="text-[11px] text-slate-400 -mt-4 transition-all duration-200">
          {meta.hint}
        </p>
        {SlashMenuPortal}

        {/* Quick-launch pills — max 3, mode-specific */}
        <div className="flex flex-wrap justify-center gap-2">
          {quickSkills.map((s, i) => (
            <QuickPill key={`${s.id}-${i}`} skill={s} mode={mode} onClick={handleQuickSkill} />
          ))}
        </div>
      </div>

      {/* ── Suggested skills rail ────────────────────────────────────── */}
      <SuggestedSkillsRail
        mode={mode}
        clientUid={clientUid ?? undefined}
        personaId={personaId ?? undefined}
        personas={personas}
        personasLoading={personasLoading}
        onSelectPersona={(id) => setPersona(id)}
        onLaunch={handleTileLaunch}
      />

      {/* ── Revenue intelligence strip — shown when client is selected ── */}
      {clientUid && (
        <RevenueIntelligenceStrip clientUid={clientUid} />
      )}
    </div>
  );
}
